import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EntraIdAuthService } from '@ird/ng-entra-id-auth';
import moment from 'moment';
import { BehaviorSubject, firstValueFrom, map, Observable, ReplaySubject, Subscription } from 'rxjs';
import { Login } from '../models';
import { LoginRepositoryHttp } from '../repositories';
import { StringUtils } from '../utils';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  public static readonly LS_TOKEN_KEY = 'token';
  public static readonly LS_TOKEN_EXPIRATION_KEY = 'expires_at';
  private storage = localStorage;
  private isLoggedInWithEntraId: boolean;
  private loginSuccess$ = new ReplaySubject<Login>();
  private logoutEmitter$ = new EventEmitter<any>();
  private _loggedIn$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private loginRepositoryHttp: LoginRepositoryHttp,
    private readonly entraIdAuthService: EntraIdAuthService,
    private router: Router,
  ) {
    this.entraIdAuthService
      .isLoggedIn()
      .pipe()
      .subscribe((loggedIn) => {
        this.isLoggedInWithEntraId = loggedIn;
        this._loggedIn$.next(loggedIn);
      });

    this.setStorage(!!sessionStorage.getItem(LoginService.LS_TOKEN_KEY));

    this.checkAndEmitLoggedInState();
  }

  onLogin(): Observable<Login> {
    return this.loginSuccess$.asObservable();
  }

  onLogout(next: () => void): Subscription {
    return this.logoutEmitter$.subscribe(next);
  }

  /**
   * Logs the user into SCS
   *
   * @param username Username
   * @param password Password
   * @returns User login response stream
   */
  login(username: string, password: string): Observable<Login> {
    return this.loginRepositoryHttp.login(username, password).pipe(
      map((response) => {
        if (response.authentication.token) {
          this.isLoggedInWithEntraId = false;
          this.storeLogin(response);
          this._loggedIn$.next(true);
          this.loginSuccess$.next(response);
        }

        return response;
      }),
    );
  }

  loginV4(username: string, password: string): Observable<Login> {
    return this.loginRepositoryHttp.loginV4(username, password).pipe(
      map((response) => {
        if (response.authentication.token) {
          this.isLoggedInWithEntraId = false;
          this.storeToken(response.authentication.token, response.authentication.validTo);
          this._loggedIn$.next(true);
          this.loginSuccess$.next(response);
        }
        return response;
      }),
    );
  }

  isTokenValid(): boolean {
    const token = this.storage.getItem(LoginService.LS_TOKEN_KEY);
    const expiresAt = this.storage.getItem(LoginService.LS_TOKEN_EXPIRATION_KEY);
    if (token && expiresAt && !this.isLoggedInWithEntraId) {
      const isValidLogin = this.checkExpiration(expiresAt);
      this._loggedIn$.next(isValidLogin);
      return isValidLogin;
    } else if (this.isLoggedInWithEntraId) {
      return this.entraIdAuthService.validateToken();
    } else {
      return false;
    }
  }

  /**
   * Returns the token if the token is valid
   * @returns string
   */
  getToken(): string | null {
    return this.isTokenValid() ? this.storage.getItem(LoginService.LS_TOKEN_KEY) : null;
  }

  public getExpirationTime(): string | null {
    return this.isTokenValid() ? this.storage.getItem(LoginService.LS_TOKEN_EXPIRATION_KEY) : null;
  }

  getImpersonatedToken(username: string): Observable<string> {
    return this.loginRepositoryHttp.getImpersonatedToken(username);
  }

  async logout(disableNavigation?: boolean): Promise<boolean> {
    try {
      await firstValueFrom(this.loginRepositoryHttp.logout());
      this._loggedIn$.next(false);
      if (this.isLoggedInWithEntraId) {
        this.entraIdAuthService.logoutSilent();
      }
    } catch (error) {
      console.error(error);
    }

    this.clearToken();
    localStorage.removeItem('user');
    localStorage.removeItem('imp');
    this.logoutEmitter$.next(undefined);

    if (!disableNavigation) {
      return this.router.navigateByUrl('/login');
    } else {
      return Promise.resolve(true);
    }
  }

  public isLoggedIn() {
    return this._loggedIn$.asObservable();
  }

  public storeLogin(login: Login): void {
    this.storeToken(login.authentication.token, login.authentication.validTo);
  }

  public storeToken(token: string, validTo: number): void {
    this.storage.setItem(LoginService.LS_TOKEN_KEY, token);
    this.storage.setItem(LoginService.LS_TOKEN_EXPIRATION_KEY, validTo.toString());
  }

  public clearToken(): void {
    this.storage.removeItem(LoginService.LS_TOKEN_KEY);
    this.storage.removeItem(LoginService.LS_TOKEN_EXPIRATION_KEY);
  }

  public checkAndEmitLoggedInState() {
    this._loggedIn$.next(this.isTokenValid());
  }

  public useLocalStorage(): void {
    this.storage = localStorage;
  }

  public useSessionStorage(): void {
    this.storage = sessionStorage;
  }

  private checkExpiration(expiration: string): boolean {
    if (StringUtils.isNumeric(expiration)) {
      expiration = JSON.parse(expiration);
    }

    const isValid = moment().isBefore(moment(expiration));

    if (!isValid) {
      this.clearToken();
    }

    return isValid;
  }

  private setStorage(useSessionStorage = false): void {
    if (useSessionStorage) {
      this.useSessionStorage();
    } else {
      this.useLocalStorage();
    }
  }
}
